import {
  IGCCClientTax,
  TaxType,
  TicketItemEGPTax,
} from "adminPanel/settings/settingTaxes/ITaxSystem";
import { IUnitsGroup } from "adminPanel/settings/units/IUnitsGroup";
import { IExternalOperation } from "amnPos/shiftsAndExternals/externalOperations/ExternalOperationData";
import { IAllowedApproval } from "amnPos/ticket/Ticket";
import { RestTableDto } from "amnRestaurants/tableOrders/restTable";
import {
  EmptyDeliveryOrderInfo,
  IDeliveryOrderInfo,
} from "amnShared/interfaces/IDeliveryOrderInfo";
import {
  EmptyPickUpOrderInfo,
  IPickUpOrderInfo,
} from "amnShared/interfaces/IPickUpOrderInfo";
import { store } from "app/store";
import { EGPSubmissionState } from "helpers/egpTaxesReceiptsHelper";
import {
  TransactionTypes,
  TicketStatus,
  OrderType,
} from "models/common-props/TransactionTypes";
import { TicketPaymentGetway } from "models/PaymentModel";
import { NIL } from "uuid";

export interface IItemDetail {
  item: string;
  quantity: number;
  unit: string;
  price: number;
  showQuantity: () => boolean;
  showPrice: () => boolean;
  subItem: IItemDetail[];
}
export interface IClientOrdersCount {
  deliveryOrdersCount: number;
  pickUpOrdersCount: number;
}

export interface ITableOrderInfo {
  id: string;
  sectionName: string;
  tableNumber: number;
  mergedTableNumbers?: string;
}
export interface ITableInfo {
  id: string;
  sectionName?: string;
  sectionId: string;
  tableNumber?: number;
  guestCount: number;
  mergedTablesNumber: number[];
  name?: string;
}
export default interface Ticket {
  id: string;
  number: number;
  code: string;
  note: string;
  total: number;
  totalPaid: number;
  taxTotal: number;
  type: TransactionTypes;
  orderType: OrderType;
  isDiscountPercentage: boolean;
  discountValue: number;
  status: TicketStatus;
  additionTime: Date | string;
  shiftId?: string;
  shiftCode: string;
  posId: string;
  pos?: string;
  employeeId: string;
  employee: string;
  employeeCode: string;
  salesmanId: string | null;
  salesman: string | null;
  clientId: string | null;
  client: string | null;
  clientLastModified: string;
  tableOrderId: string | null;
  tableOrderInfo: ITableOrderInfo | null;
  ticketPaymentGetways: TicketPaymentGetway[];
  ticketItems: TicketItem[];
  deliveryOrderInfo?: IDeliveryOrderInfo | null;
  pickupOrderInfo?: IPickUpOrderInfo | null;
  externalOperations?: IExternalOperation[];
  gccTax?: IGCCClientTax;
  prevTotalPaid?: number;
  cancelledTime?: Date;
  tableInfo?: ITableInfo;
  clientPhone?: string;
  allowedApprovals?: IAllowedApproval[];
  localAdministrationFee?: TicketSYRLocalAdministrationFee | null;
  ticketSYRRestaurantTaxes?: TicketSYRRestaurantTax[];
  egpSubmissionState?: EGPSubmissionState;
  isTicketBeforeEnableTaxesSubscriptionDate: boolean;
}

export enum ItemBehavior {
  Inventory,
  Service,
}

export interface TicketItem {
  id: string;
  number: number;
  itemType: number;
  itemBehavior: ItemBehavior;
  ticketId: string;
  menuItemId: string;
  itemId: string;
  preparedMaterialId: string;
  comboId: string;
  item: string | null;
  image?: string;
  price: number;
  totalUnitPrice: number;
  qty: number;
  discountPercent: number;
  discountValue: number;
  giftQty: number;
  value: number;
  total: number;
  net: number;
  unit: string | null;
  unitId: string;
  salesmanId: string | null;
  salesman: string | null;
  isReseted: boolean;
  barcode?: string;
  giftsValue: number;
  giftsPercent: number;
  menuId: string;
  modifierTicketItems: ModifierTicketItem[];
  description?: string;
  isDisabled?: boolean;
  unitsGroup?: IUnitsGroup;
  taxValue: number;
  taxRatio: number;
  note: string;
  taxType?: TaxType;
  ticketItemEGPTaxesDetails: TicketItemEGPTax[];
  consumerTaxPercentage?: number | null;
}

export interface TicketSYRLocalAdministrationFee {
  feePercentage?: number | null;
  roundUpTo?: number | null;
  value?: number | null;
}

export interface TicketSYRRestaurantTax {
  id: string;
  ticketId: string;
  syrRestaurantTaxId: string;
  taxName: string;
  taxPercentage: number;
  roundUpTo: number;
  taxValue?: number | null;
}

export enum TicketModifierItemType {
  PreparedFixedIngredient,
  PrepIncreaseDecrease,
  PrepAlternative,
  PrepMultipleAlternative,
  ComboFixedItems,
  ComboAlternatives,
  ComboGeneralModifiers,
}

export interface ModifierTicketItem {
  id: string;
  modifierRowItemId: string;
  number: number;
  ticketItemId: string;
  modifierId: string;
  modifierName: string;
  itemId: string;
  item: string | null;
  image?: string;
  price: number;
  extraPrice: number;
  maximumMultiple: number;
  maximumDefault: number;
  idontwant: boolean;
  idontwantDefault: boolean;
  quantity: number;
  stepQuantity: number;
  startsAt: number;
  value: number;
  net: number;
  // taxRatio: number;
  // taxValue: number;
  total: number;
  unit: string | null;
  unitId: string;
  modifierType: TicketModifierItemType;
  subModifierTicketItemDtos: ModifierTicketItem[];
  isDefault: boolean;
  isChangeQuantity: boolean;
  note: string;
  sortOrder: number;
  // taxType?: TaxType;
  // ticketItemEGPTaxesDetails: TicketItemEGPTax[];
}

export const EmptyModifierTicketItem: ModifierTicketItem = {
  id: NIL,
  modifierRowItemId: NIL,
  number: 0,
  ticketItemId: NIL,
  modifierId: NIL,
  modifierName: "",
  itemId: NIL,
  item: "",
  image: "",
  price: 0,
  quantity: 0,
  stepQuantity: 0,
  startsAt: 0,
  maximumMultiple: 0,
  maximumDefault: 0,
  extraPrice: 0,
  value: 0,
  net: 0,
  // taxRatio: 0,
  // taxValue: 0,
  total: 0,
  idontwant: false,
  idontwantDefault: false,
  unit: "",
  unitId: NIL,
  modifierType: TicketModifierItemType.PrepIncreaseDecrease,
  subModifierTicketItemDtos: [],
  isDefault: false,
  isChangeQuantity: false,
  note: "",
  sortOrder: 0,
  // ticketItemEGPTaxesDetails: [],
};

export interface SubModifierTicketItem {
  id: string;
  number: number;
  modifierTicketItemId: string;
  itemId: string;
  item: string | null;
  image?: string;
  price: number;
  qty: number;
  value: number;
  total: number;
  unit: string | null;
  unitId: string;
  ticketItemEGPTaxesDetails: TicketItemEGPTax[];
}

export const emptyTicketItem: TicketItem = {
  id: NIL,
  number: 0,
  itemBehavior: ItemBehavior.Inventory,
  itemType: 0,
  menuItemId: NIL,
  ticketId: NIL,
  itemId: NIL,
  preparedMaterialId: NIL,
  comboId: NIL,
  item: "",
  image: "",
  price: 0,
  totalUnitPrice: 0,
  qty: 0,
  discountPercent: 0,
  discountValue: 0,
  giftQty: 0,
  value: 0,
  total: 0,
  net: 0,
  taxValue: 0,
  unit: "",
  unitId: NIL,
  salesmanId: NIL,
  salesman: "",
  isReseted: false,
  barcode: "",
  giftsValue: 0,
  giftsPercent: 0,
  menuId: NIL,
  modifierTicketItems: [],
  description: "",
  isDisabled: false,
  taxRatio: 0,
  note: "",
  ticketItemEGPTaxesDetails: [],
  consumerTaxPercentage: null,
};

export interface ItemDiscount {
  discPercent?: number;
  discValue?: number;
}

export const ticketDto = (ticket: Ticket) => {
  return {
    ...ticket,
    salesman: null,
    salesmanId: null,
    client: null,
    employeeId: store.getState().employeeSettings.id,
    shiftId: store.getState().shiftInfo.id,
    shift: null,

    ticketItems: ticket.ticketItems.map((item, index) => {
      return {
        ...item,
        number: index,
        ticketId: ticket.id,
        salesman: null,
        salesmanId: null,
        unit: null,
        item: null,
      };
    }),
  } as Ticket;
};

export interface ITicketItemsTotals {
  subTotal: number;
  itemDiscount: number;
  vat: number;
  total: number;
}

export const emptyTicketItemsTotals: ITicketItemsTotals = {
  subTotal: 0,
  itemDiscount: 0,
  vat: 0,
  total: 0,
};

export interface ITicketPaymentsTotals {
  transactionValue: number;
  collected: number;
  deferredAmount: number;
}

export const emptyTicketPaymentsTotals: ITicketPaymentsTotals = {
  transactionValue: 0,
  collected: 0,
  deferredAmount: 0,
};

export const emptyTicket: Ticket = {
  id: NIL,
  number: 0,
  code: "",
  note: "",
  total: 0,
  totalPaid: 0,
  taxTotal: 0,
  type: TransactionTypes.sales,
  orderType: OrderType.fastService,
  shiftId: NIL,
  isDiscountPercentage: false,
  discountValue: 0,
  status: TicketStatus.new,
  additionTime: "",
  shiftCode: "",
  posId: "",
  pos: "",
  employeeId: NIL,
  employee: "",
  salesmanId: null,
  salesman: null,
  clientId: null,
  client: null,
  tableOrderId: null,
  ticketPaymentGetways: [],
  ticketItems: [],
  deliveryOrderInfo: null,
  pickupOrderInfo: null,
  clientLastModified: "",
  tableOrderInfo: null,
  employeeCode: "",
  clientPhone: "",
  allowedApprovals: [],
  localAdministrationFee: null,
  ticketSYRRestaurantTaxes: [],
  isTicketBeforeEnableTaxesSubscriptionDate: true,
};

export type CustomizedTicketDocuments = { [key: string]: string };

export function mapToItemDetail(
  modifierTicketItem: ModifierTicketItem,
): IItemDetail {
  const {
    item,
    quantity,
    unit,
    extraPrice,
    net,
    modifierType,
    isChangeQuantity,
    startsAt,
    subModifierTicketItemDtos,
  } = modifierTicketItem;

  return {
    item: item || "",
    quantity: quantity,
    unit: unit || "",
    price: net,
    subItem: subModifierTicketItemDtos
      ? subModifierTicketItemDtos.map(mapToItemDetail)
      : [],
    showQuantity: () => {
      switch (modifierType) {
        case TicketModifierItemType.PrepIncreaseDecrease:
          return true;
        case TicketModifierItemType.PrepAlternative:
          return false;
        case TicketModifierItemType.PrepMultipleAlternative:
          return isChangeQuantity;
        case TicketModifierItemType.ComboFixedItems:
          return true;
        case TicketModifierItemType.ComboAlternatives:
          return false;
        case TicketModifierItemType.ComboGeneralModifiers:
          return false;
        default:
          return false;
      }
    },
    showPrice: () => {
      switch (modifierType) {
        case TicketModifierItemType.PrepIncreaseDecrease:
          return extraPrice.isGreaterThan(0);
        case TicketModifierItemType.PrepAlternative:
          return net.isGreaterThan(0);
        case TicketModifierItemType.PrepMultipleAlternative:
          return (
            extraPrice.isGreaterThan(0) &&
            quantity.isGreaterThanOrEqualTo(startsAt)
          );
        case TicketModifierItemType.ComboFixedItems:
          net.isGreaterThan(0);
        case TicketModifierItemType.ComboAlternatives:
          return net.isGreaterThan(0);
        case TicketModifierItemType.ComboGeneralModifiers:
          return extraPrice.isGreaterThan(0);
        default:
          return false;
      }
    },
  };
}
